// src/components/common/BaseDeleteDialog.tsx 
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import {
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { 
  useTranslate, 
  useDataProvider, 
  useNotify, 
  useRefresh,
  RaRecord 
} from 'react-admin';

export interface BaseDeleteDialogProps {
  /** Si el diálogo está abierto */
  open: boolean;
  /** Función para cerrar el diálogo */
  onClose: () => void;
  /** Función personalizada después de eliminar */
  onConfirm?: () => void;
  /** Recurso de React Admin */
  resource: string;
  /** ID del registro a eliminar */
  recordId: string | number;
  /** Datos del registro */
  record?: RaRecord;
  /** Título personalizado */
  title?: string;
  /** Mensaje personalizado */
  message?: string;
  /** Mensaje de advertencia */
  warningMessage?: string;
  /** Contenido adicional */
  children?: React.ReactNode;
}

/**
 * BaseDeleteDialog - Migración del componente existente a TypeScript
 * Mantiene toda la funcionalidad original
 */
const BaseDeleteDialog: React.FC<BaseDeleteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  resource,
  recordId,
  record,
  title,
  message,
  warningMessage,
  children
}) => {
  const [loading, setLoading] = useState(false);
  
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const defaultTitle = translate('ra.message.delete_title', {
    name: translate(`resources.${resource}.name`, resource),
    id: recordId,
  });

  const defaultMessage = translate('ra.message.delete_content');
  const defaultWarningMessage = translate('common.warnings.delete_irreversible', 'This action is irreversible.');

  const handleDelete = async () => {
    try {
      setLoading(true);
      
      await dataProvider.delete(resource, {
        id: recordId,
        previousData: record,
      });

      notify(
        translate('ra.notification.deleted', {
          smart_count: 1,
        }),
        { type: 'success' }
      );

      refresh();
      
      if (onConfirm) {
        onConfirm();
      }
      
      onClose();
    } catch (error: any) {
      console.error('Delete error:', error);
      
      // Manejo de errores específicos (mantiene lógica original)
      if (error.status === 409) {
        const errorMessage = translate(`resources.${resource}.errors.has_dependencies`) || 
                           'Cannot delete - has dependencies';
        notify(errorMessage, { type: 'error' });
      } else {
        const errorMessage = translate(`resources.${resource}.errors.delete_failed`) || 
                           'Delete failed';
        notify(errorMessage, { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title || defaultTitle}</DialogTitle>
      
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {message || defaultMessage}
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warningMessage || defaultWarningMessage}
        </Alert>

        {children}
      </DialogContent>
      
      <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
        <Button 
          onClick={onClose} 
          disabled={loading} 
          variant="outlined"
        >
          {translate('ra.action.cancel', 'Cancel')}
        </Button>
        
        <Button 
          onClick={handleDelete} 
          disabled={loading} 
          variant="contained"
          color="error"
          startIcon={loading ? null : <DeleteIcon />}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Deleting...
            </Box>
          ) : (
            translate('ra.action.delete', 'Delete')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BaseDeleteDialog;